import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  Typography,
  createFilterOptions,
  useTheme,
} from "@mui/material";
import {ChevronDown, Plus} from "lucide-react";
import {AutocompleteInput} from "@/components/input/Autocomplete.tsx";
import {
  RealEstateBrokerFragmentFragment,
  RealEstateTransactionSpecialistFragmentFragment,
  useSearchRealEstateBrokersQuery,
  useSearchRealEstateTransactionSpecialistsQuery,
  useSetRealEstateBrokerToZyusetuMutation,
  useSetRealEstateTransactionSpecialistToZyusetuMutation,
  ZyusetuFragmentFragment,
} from "@/graphql/graphql";
import {useState} from "react";
import toast from "react-hot-toast";
import {CreateNewRealEstateBrokerDialog} from "@/components/dialog/CreateNewRealEstateBrokerDialog.tsx";
import {CreateNewRealEstateTransactionSpecialistDialog} from "@/components/dialog/CreateNewRealEstateTransactionSpecialistDialog.tsx";
import {useQueryClient} from "@tanstack/react-query";

interface ZyusetuBlockerSelectProps {
  zyusetu: ZyusetuFragmentFragment;
}

const brokerFilter = createFilterOptions<RealEstateBrokerFragmentFragment>();
const specialistFilter = createFilterOptions<RealEstateTransactionSpecialistFragmentFragment>();

export function ZyusetuBrokerSelect({zyusetu}: ZyusetuBlockerSelectProps) {
  const [blockerSearchString, setBlockerSearchString] = useState("");
  const [blockers, setBlockers] = useState<RealEstateBrokerFragmentFragment[]>(
    zyusetu.realEstateBrokers,
  );
  const searchBrokers = useSearchRealEstateBrokersQuery({
    searchString: blockerSearchString,
  });
  const setBrokersApi = useSetRealEstateBrokerToZyusetuMutation();
  
  const [specialistSearchString, setSpecialistSearchString] = useState("");
  const [specialists, setSpecialists] = useState<
    RealEstateTransactionSpecialistFragmentFragment[]
  >(zyusetu.realEstateTransactionSpecialists);
  
  const searchSpecialist = useSearchRealEstateTransactionSpecialistsQuery({
    searchString: specialistSearchString,
  });
  
  const setSpecialistsApi =
    useSetRealEstateTransactionSpecialistToZyusetuMutation();
  
  const [openCreateBrokerDialog, setOpenCreateBrokerDialog] = useState(false);
  const [openCreateSpecialistDialog, setOpenCreateSpecialistDialog] = useState(false);
  
  const queryClient = useQueryClient();
  const brokerQueryKey = useSearchRealEstateBrokersQuery.getKey({
    searchString: blockerSearchString,
  });
  const specialistQueryKey = useSearchRealEstateTransactionSpecialistsQuery.getKey({
    searchString: specialistSearchString,
  });
  
  const theme = useTheme();
  
  return (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ChevronDown/>} tabIndex={-1}>
        <Stack>
          <Typography variant={"subtitle2"}>宅建業者を選択</Typography>
          <Typography variant={"body2"}>
            この物件に関連する宅建業者を選択してください
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          {openCreateBrokerDialog && (
            <CreateNewRealEstateBrokerDialog
              open={openCreateBrokerDialog}
              onClose={() => setOpenCreateBrokerDialog(false)}
              onCreateSuccess={async (value) => {
                const newBlockers = [...blockers, value];
                await setBrokersApi
                  .mutateAsync({
                    zyusetuId: zyusetu.id,
                    brokerIds: newBlockers.map((v) => v.id),
                  })
                  .then(() => {
                    toast.success("宅建業者を登録しました");
                    setBlockers(newBlockers);
                  })
                  .catch(() => toast.error("宅建業者の登録に失敗しました"));
                
                queryClient.setQueryData(brokerQueryKey, {
                  searchRealEstateBrokers: [
                    ...(searchBrokers.data?.searchRealEstateBrokers ?? []),
                    value,
                  ],
                });
              }}
            />
          )}
          
          {openCreateSpecialistDialog && (
            <CreateNewRealEstateTransactionSpecialistDialog
              open={openCreateSpecialistDialog}
              onClose={() => setOpenCreateSpecialistDialog(false)}
              onCreateSuccess={async (value) => {
                const newSpecialists = [...specialists, value];
                await setSpecialistsApi
                  .mutateAsync({
                    zyusetuId: zyusetu.id,
                    specialistIds: newSpecialists.map((v) => v.id),
                  })
                  .then(() => {
                    toast.success("宅建取引士を登録しました");
                    setSpecialists(newSpecialists);
                  })
                  .catch(() => toast.error("宅建取引士の登録に失敗しました"));
                
                queryClient.setQueryData(specialistQueryKey, {
                  searchRealEstateTransactionSpecialists: [
                    ...(searchSpecialist.data?.searchRealEstateTransactionSpecialists ?? []),
                    value,
                  ],
                });
              }}
            />
          )}
          
          <Typography variant={"body2"} color={"textSecondary"}>
            宅地建物取引業者
          </Typography>
          <AutocompleteInput
            onChange={async (v) => {
              if (v.length >= 3) {
                toast.error("宅建業者は2つまでしか選択できません");
                return;
              }
              
              // 新規作成オプションが選択された場合
              if (v.some(item => item.id === "new")) {
                setOpenCreateBrokerDialog(true);
                return;
              }
              
              await setBrokersApi
                .mutateAsync({
                  zyusetuId: zyusetu.id,
                  brokerIds: v.map((v) => v.id),
                })
                .then(() => {
                  toast.success("宅建業者を登録しました");
                  setBlockers(v);
                })
                .catch(() => toast.error("宅建業者の登録に失敗しました"));
            }}
            loading={searchBrokers.isLoading}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            value={blockers}
            multiple={true}
            getOptionLabel={(option) =>
              option.id === "new" ? "新規追加" : `${option.name}(${option.representativeName})`
            }
            fetchOptions={async (words: string) => {
              setBlockerSearchString(words);
            }}
            filterOptions={(options, params) => {
              const filtered = brokerFilter(options, params);
              // 型アサーションを使用して、必要なプロパティを持つオブジェクトとして扱う
              filtered.push({
                name: "新規追加", 
                id: "new",
                licenseNumber: "",
                mainOfficeAddress: "",
                phoneNumber: "",
                representativeName: ""
              } as RealEstateBrokerFragmentFragment);
              return filtered;
            }}
            renderOption={(props, option) => {
              if (option.id === "new") {
                return (
                  <li
                    {...props}
                    style={{
                      color: theme.palette.primary.main,
                    }}
                  >
                    <Plus/>
                    新規追加
                  </li>
                );
              }
              
              return (
                <li {...props}>
                  {option.name}({option.representativeName})
                </li>
              );
            }}
            options={searchBrokers.data?.searchRealEstateBrokers || []}
          />
          <Typography variant={"body2"} color={"textSecondary"}>
            説明をする宅地建物取引士
          </Typography>
          <AutocompleteInput
            onChange={async (v) => {
              if (v.length >= 3) {
                toast.error("宅建取引士は2つまでしか選択できません");
                return;
              }
              
              // 新規作成オプションが選択された場合
              if (v.some(item => item.id === "new")) {
                setOpenCreateSpecialistDialog(true);
                return;
              }
              
              await setSpecialistsApi
                .mutateAsync({
                  zyusetuId: zyusetu.id,
                  specialistIds: v.map((v) => v.id),
                })
                .then(() => {
                  toast.success("宅建取引士を登録しました");
                  setSpecialists(v);
                })
                .catch(() => toast.error("宅建取引士の登録に失敗しました"));
            }}
            loading={searchSpecialist.isLoading}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            value={specialists}
            multiple={true}
            getOptionLabel={(option) =>
              option.id === "new" ? "新規追加" : `${option.name}(${option.registrationNumber})`
            }
            fetchOptions={async (words: string) => {
              setSpecialistSearchString(words);
            }}
            filterOptions={(options, params) => {
              const filtered = specialistFilter(options, params);
              // 型アサーションを使用して、必要なプロパティを持つオブジェクトとして扱う
              filtered.push({
                name: "新規追加", 
                id: "new",
                registrationNumber: "",
                officeAddress: "",
                officeName: "",
                phoneNumber: ""
              } as RealEstateTransactionSpecialistFragmentFragment);
              return filtered;
            }}
            renderOption={(props, option) => {
              if (option.id === "new") {
                return (
                  <li
                    {...props}
                    style={{
                      color: theme.palette.primary.main,
                    }}
                  >
                    <Plus/>
                    新規追加
                  </li>
                );
              }
              
              return (
                <li {...props}>
                  {option.name}({option.registrationNumber})
                </li>
              );
            }}
            options={
              searchSpecialist.data?.searchRealEstateTransactionSpecialists ||
              []
            }
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
